import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Alert, AlertDescription } from './ui/alert';
import { authAPI } from '../services/api';
import { Loader2, UserPlus, Mail, Phone, Calendar, Landmark, Coins, Eye, EyeOff, Check, X, Circle, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import useServerValidation from '../hooks/useServerValidation';

const AddUserModal = ({ onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const { applyServerErrors } = useServerValidation({ setErrors });

    // Signup State
    const [signupData, setSignupData] = useState({
        firstName: '',
        lastName: '',
        contactNumber: '',
        alternateContactNumber: '',
        govIdType: '',
        govIdNumber: '',
        username: '',
        password: '',
        email: '',
        dateOfJoining: new Date().toISOString().split('T')[0],
        annualSalaryInr: '',
        isOwnerFlg: false,
        ownerType: ''
    });

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSignupData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const checkUsernameStrength = (username) => {
        return {
            hasValidChars: /^[a-zA-Z0-9._@&-]*$/.test(username),
            hasNoSpaces: !/\s/.test(username),
            hasMaxLength: username.length <= 50,
            isNotEmpty: username.length > 0
        };
    };

    const checkPasswordStrength = (password) => {
        return {
            hasUpperCase: /[A-Z]/.test(password),
            hasLowerCase: /[a-z]/.test(password),
            hasNumber: /[0-9]/.test(password),
            hasSpecialChar: /[^a-zA-Z0-9\s]/.test(password),
            hasMinLength: password.length >= 8,
            isNotEmpty: password.length > 0
        };
    };

    const passwordStrength = checkPasswordStrength(signupData.password);
    const usernameStrength = checkUsernameStrength(signupData.username);

    const handleSignup = async (e) => {
        // ... (existing handleSignup code)
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const payload = {
                ...signupData,
                annualSalaryInr: parseFloat(signupData.annualSalaryInr),
                // Send null if not owner or if ownerType is empty string to avoid Enum deserialization errors
                ownerType: signupData.isOwnerFlg && signupData.ownerType ? signupData.ownerType : null
            };

            await authAPI.signup(payload);
            if (onSuccess) onSuccess(signupData.username);
            onClose();
        } catch (err) {
            const serverErrors = applyServerErrors(err);
            setError(serverErrors._form || err.response?.data?.message || 'Registration failed. Please check your details.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-[800px] max-h-[95vh] overflow-hidden flex flex-col p-0 transition-all duration-500 rounded-[3rem] border-0 shadow-2xl ring-1 ring-white/20 dark:bg-slate-950 dark:ring-white/10">
                <DialogHeader className="p-8 pb-4 border-b bg-white/40 dark:bg-slate-900/60 dark:border-slate-800 backdrop-blur-3xl sticky top-0 z-40">
                    <div className="flex items-center gap-5">
                        <div className="relative group">
                            <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                            <div className="relative p-3.5 rounded-2xl bg-orange-100 text-[#ff6f00] shadow-sm transform hover:rotate-3 transition-transform duration-300">
                                <UserPlus className="w-8 h-8" />
                            </div>
                        </div>
                        <div>
                            <DialogTitle className="text-3xl font-black tracking-tight text-slate-800 dark:text-slate-100 flex items-center gap-2">
                                New Personnel
                                <span className="text-sm font-black px-3 py-1 bg-orange-100 text-orange-600 rounded-full uppercase tracking-widest ml-1 shadow-sm">Onboarding</span>
                            </DialogTitle>
                            <DialogDescription className="text-sm font-bold text-slate-500 mt-1 opacity-80">
                                Expand the horizons of your team within the JET INVENTORY system.
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <form onSubmit={handleSignup} className="contents" autoComplete="off">
                    {/* Fake hidden inputs to trick Chrome's autofill heuristic */}
                    <input type="text" className="hidden" aria-hidden="true" />
                    <input type="password" name="fake-password" style={{ display: 'none' }} aria-hidden="true" />

                    <div className="flex-1 overflow-y-auto px-8 py-8 scroll-smooth">
                        {error && (
                            <div className="mb-8 p-0 animate-in slide-in-from-top-4 duration-500">
                                <div className="flex items-start gap-4 p-5 rounded-2xl border bg-rose-50 border-rose-100 text-rose-900 shadow-lg shadow-black/5">
                                    <div className="p-2 rounded-xl shrink-0 mt-0.5 bg-rose-100/50">
                                        <AlertCircle className="h-5 w-5 text-rose-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-black uppercase tracking-wider leading-none mb-1.5 pt-1.5">Registration Error</p>
                                    <p className="text-xs font-bold leading-relaxed opacity-80 break-words">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="firstName"
                                name="firstName"
                                placeholder="John"
                                value={signupData.firstName}
                                onChange={handleChange}
                                className={cn("jit-input", errors.firstName && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                required
                                autoComplete="off"
                            />
                            {errors.firstName && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
                            <Input
                                id="lastName"
                                name="lastName"
                                placeholder="Doe"
                                value={signupData.lastName}
                                onChange={handleChange}
                                className={cn("jit-input", errors.lastName && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                required
                                autoComplete="off"
                            />
                            {errors.lastName && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Email <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder="john@example.com"
                                    value={signupData.email}
                                    onChange={handleChange}
                                    className={cn("pl-9 jit-input", errors.email && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    required
                                    autoComplete="off"
                                />
                            </div>
                            {errors.email && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="contactNumber" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Contact Number <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="contactNumber"
                                    name="contactNumber"
                                    placeholder="1234567890"
                                    value={signupData.contactNumber}
                                    onChange={handleChange}
                                    className={cn("pl-9 jit-input", errors.contactNumber && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    required
                                    autoComplete="off"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                />
                            </div>
                            {errors.contactNumber && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="alternateContactNumber" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Alternate Contact Number (Optional)</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="alternateContactNumber"
                                    name="alternateContactNumber"
                                    placeholder="0987654321"
                                    value={signupData.alternateContactNumber || ''}
                                    onChange={handleChange}
                                    className="pl-9 jit-input"
                                    autoComplete="off"
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="dateOfJoining" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Date of Joining <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="dateOfJoining"
                                    name="dateOfJoining"
                                    type="date"
                                    value={signupData.dateOfJoining}
                                    onChange={handleChange}
                                    className={cn("pl-9 jit-input", errors.dateOfJoining && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    required
                                />
                            </div>
                            {errors.dateOfJoining && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="govIdType" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Gov ID Type <span className="text-red-500">*</span></Label>
                            <Select
                                value={signupData.govIdType}
                                onValueChange={(val) => {
                                    setSignupData({ ...signupData, govIdType: val });
                                    setErrors(prev => ({ ...prev, govIdType: '' }));
                                }}
                            >
                                <SelectTrigger className={cn("jit-input", errors.govIdType && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}>
                                    <SelectValue placeholder="Select ID Type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="AadharCard">Aadhar Card</SelectItem>
                                    <SelectItem value="PanCard">Pan Card</SelectItem>
                                    <SelectItem value="Passport">Passport</SelectItem>
                                    <SelectItem value="DrivingLicense">Driving License</SelectItem>
                                </SelectContent>
                            </Select>
                            {errors.govIdType && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="govIdNumber" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Gov ID Number <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Landmark className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="govIdNumber"
                                    name="govIdNumber"
                                    placeholder="ID Number"
                                    value={signupData.govIdNumber}
                                    onChange={handleChange}
                                    className={cn("pl-9 jit-input", errors.govIdNumber && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    required
                                    autoComplete="off"
                                    inputMode="text"
                                />
                            </div>
                            {errors.govIdNumber && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="username" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Username <span className="text-red-500">*</span></Label>
                            <Input
                                id="username"
                                name="username"
                                placeholder="Username"
                                value={signupData.username}
                                onChange={handleChange}
                                className={cn("jit-input", errors.username && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                required
                                autoComplete="off"
                            />
                            {errors.username && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                            <div className="space-y-1 mt-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md border dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                                <p className="font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-tight opacity-70">Username Requirements:</p>
                                <div className={`flex items-center gap-2 ${!usernameStrength.isNotEmpty ? 'text-slate-500' : (usernameStrength.hasValidChars ? 'text-green-600' : 'text-red-500')}`}>
                                    {!usernameStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (usernameStrength.hasValidChars ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>Allowed characters (a-z, 0-9, . _ @ & -)</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!usernameStrength.isNotEmpty ? 'text-slate-500' : (usernameStrength.hasNoSpaces ? 'text-green-600' : 'text-red-500')}`}>
                                    {!usernameStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (usernameStrength.hasNoSpaces ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>No spaces allowed</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!usernameStrength.isNotEmpty ? 'text-slate-500' : (usernameStrength.hasMaxLength ? 'text-green-600' : 'text-red-500')}`}>
                                    {!usernameStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (usernameStrength.hasMaxLength ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>Max 50 characters</span>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Password <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="••••••••"
                                    value={signupData.password}
                                    onChange={handleChange}
                                    required
                                    className={cn("pr-10 jit-input", errors.password && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                            {errors.password && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                            <div className="space-y-1 mt-2 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-md border dark:border-slate-800 text-xs text-slate-500 dark:text-slate-400">
                                <p className="font-bold text-slate-700 dark:text-slate-300 mb-2 uppercase tracking-tight opacity-70">Password Requirements:</p>
                                <div className={`flex items-center gap-2 ${!passwordStrength.isNotEmpty ? 'text-slate-500' : (passwordStrength.hasLowerCase ? 'text-green-600' : 'text-red-500')}`}>
                                    {!passwordStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (passwordStrength.hasLowerCase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>At least one lowercase letter</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!passwordStrength.isNotEmpty ? 'text-slate-500' : (passwordStrength.hasUpperCase ? 'text-green-600' : 'text-red-500')}`}>
                                    {!passwordStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (passwordStrength.hasUpperCase ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>At least one uppercase letter</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!passwordStrength.isNotEmpty ? 'text-slate-500' : (passwordStrength.hasNumber ? 'text-green-600' : 'text-red-500')}`}>
                                    {!passwordStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (passwordStrength.hasNumber ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>At least one number</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!passwordStrength.isNotEmpty ? 'text-slate-500' : (passwordStrength.hasSpecialChar ? 'text-green-600' : 'text-red-500')}`}>
                                    {!passwordStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (passwordStrength.hasSpecialChar ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>At least one special character</span>
                                </div>
                                <div className={`flex items-center gap-2 ${!passwordStrength.isNotEmpty ? 'text-slate-500' : (passwordStrength.hasMinLength ? 'text-green-600' : 'text-red-500')}`}>
                                    {!passwordStrength.isNotEmpty ? <Circle className="h-3 w-3 fill-slate-300 text-slate-300" /> : (passwordStrength.hasMinLength ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />)}
                                    <span>Minimum 8 characters</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="annualSalaryInr" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Annual Salary (INR) <span className="text-red-500">*</span></Label>
                            <div className="relative">
                                <Coins className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                                <Input
                                    id="annualSalaryInr"
                                    name="annualSalaryInr"
                                    type="number"
                                    placeholder="500000"
                                    value={signupData.annualSalaryInr}
                                    onChange={handleChange}
                                    className={cn("pl-9 jit-input", errors.annualSalaryInr && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}
                                    required
                                    inputMode="numeric"
                                    pattern="[0-9]*"
                                />
                            </div>
                            {errors.annualSalaryInr && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                        </div>
                        <div className="flex items-center space-x-3 pt-4 md:col-span-2">
                            <input
                                id="isOwnerFlg"
                                type="checkbox"
                                checked={signupData.isOwnerFlg}
                                onChange={handleChange}
                                className="h-5 w-5 rounded border-gray-300 text-[#FF7D00] focus:ring-[#FF7D00] transition-all cursor-pointer"
                            />
                            <Label htmlFor="isOwnerFlg" className="cursor-pointer select-none text-xs font-black uppercase tracking-widest text-[#FF7D00]">
                                Register as Business Owner
                            </Label>
                        </div>
                        {signupData.isOwnerFlg && (
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="ownerType" className="text-xs font-black uppercase tracking-widest text-[#FF7D00]">Owner Type <span className="text-red-500">*</span></Label>
                                <Select
                                    value={signupData.ownerType}
                                    onValueChange={(val) => {
                                        setSignupData({ ...signupData, ownerType: val });
                                        setErrors(prev => ({ ...prev, ownerType: '' }));
                                    }}
                                >
                                    <SelectTrigger className={cn("jit-input", errors.ownerType && "border-red-400 bg-red-50/50 dark:bg-red-500/10 animate-shake")}>
                                        <SelectValue placeholder="Select Owner Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="NORMAL_USER">Normal User</SelectItem>
                                        <SelectItem value="PARTNERSHIP">Partnership</SelectItem>
                                    </SelectContent>
                                </Select>
                                {errors.ownerType && <p className="text-[10px] font-black text-red-500 uppercase tracking-tighter ml-1">Required</p>}
                            </div>
                        )}
                    </div>
                </div>

                <DialogFooter className="p-8 pt-4 border-t bg-slate-50/50 dark:bg-slate-900/60 dark:border-slate-800 sticky bottom-0 z-30 sm:justify-end items-center gap-4 shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.05)]">
                        <Button type="button" variant="ghost" onClick={onClose} className="h-12 px-8 rounded-xl font-bold text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100">
                            Cancel
                        </Button>
                        <Button 
                            type="submit" // Changed to submit to trigger form onSubmit
                            disabled={loading || !signupData.username || !signupData.password} // Use signupData for validation
                            className="h-12 px-12 rounded-xl bg-[#FF7D00] hover:brightness-110 active:scale-95 text-white font-black uppercase tracking-widest shadow-xl shadow-orange-500/10 transition-all duration-200"
                        >
                    {loading ? <Loader2 className="animate-spin h-5 w-5 mr-2" /> : 'Confirm Onboarding'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default AddUserModal;
